import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO, isAfter } from 'date-fns';
import { nl } from 'date-fns/locale';

export default function Dashboard({ setPage }) {
  const { currentUser, userProfile } = useAuth();
  const [planning, setPlanning] = useState([]);
  const [weekCount, setWeekCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const planSnap = await getDocs(query(
          collection(db, 'planning'),
          where('userId', '==', currentUser.uid)
        ));
        const items = planSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const upcoming = items.filter(i => {
          try { return isAfter(parseISO(i.datum), new Date()); } catch { return false; }
        }).slice(0, 3);
        setPlanning(upcoming);

        const weekSnap = await getDocs(collection(db, 'weken'));
        const openWeeks = weekSnap.docs.filter(d => d.data().open).length;
        setWeekCount(openWeeks);
      } catch (e) {
        console.log('Load error:', e);
      }
      setLoading(false);
    }
    load();
  }, [currentUser.uid]);

  const isAdmin = userProfile?.role === 'admin';
  const naam = userProfile?.voornaam ? `${userProfile.voornaam} ${userProfile.achternaam || ''}` : currentUser.email;

  const groepColors = {
    'Groen': '#2D6A4F', 'Geel': '#E67E22', 'Rood': '#C0392B',
    'Blauw': '#2980B9', 'Toekomstheld': '#8E44AD'
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">👋 Welkom, {userProfile?.voornaam || 'vrijwilliger'}!</h1>
        <p className="page-subtitle">{isAdmin ? 'Je bent ingelogd als admin' : 'Jouw persoonlijk overzicht'}</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card" style={{ borderLeft: '3px solid var(--primary)' }}>
          <div className="stat-value">{weekCount}</div>
          <div className="stat-label">Openstaande weken</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--accent)' }}>
          <div className="stat-value">{planning.length}</div>
          <div className="stat-label">Geplande shiften</div>
        </div>
        {userProfile?.groep?.length > 0 && (
          <div className="stat-card" style={{ borderLeft: '3px solid #8E44AD' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {userProfile.groep.map(g => (
                <span key={g} className="badge" style={{ background: groepColors[g] + '20', color: groepColors[g] }}>{g}</span>
              ))}
            </div>
            <div className="stat-label">Jouw groep(en)</div>
          </div>
        )}
        {userProfile?.locatie?.length > 0 && (
          <div className="stat-card" style={{ borderLeft: '3px solid var(--accent-dark)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {userProfile.locatie.map(l => (
                <span key={l} className="badge badge-gray">{l}</span>
              ))}
            </div>
            <div className="stat-label">Locatie(s)</div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Snel acties */}
        <div className="card card-padded">
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>⚡ Snelle acties</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className="btn btn-primary btn-full" onClick={() => setPage('beschikbaarheid')}>
              📅 Beschikbaarheid doorgeven
            </button>
            <button className="btn btn-secondary btn-full" onClick={() => setPage('planning')}>
              📋 Mijn planning bekijken
            </button>
            <button className="btn btn-secondary btn-full" onClick={() => setPage('profiel')}>
              👤 Profiel bewerken
            </button>
            {isAdmin && (
              <button className="btn btn-accent btn-full" onClick={() => setPage('admin-vrijwilligers')}>
                ⚙ Beheer vrijwilligers
              </button>
            )}
          </div>
        </div>

        {/* Upcoming planning */}
        <div className="card card-padded">
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>📅 Komende planning</h3>
          {loading ? (
            <p style={{ color: 'var(--text-3)', fontSize: 14 }}>Laden...</p>
          ) : planning.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-3)' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <p style={{ fontSize: 14 }}>Nog geen planning</p>
            </div>
          ) : planning.map(p => (
            <div key={p.id} className="planning-item">
              <div className="planning-dot" />
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{p.titel || 'Shift'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  {p.datum ? format(parseISO(p.datum), 'EEEE d MMMM', { locale: nl }) : ''}
                  {p.locatie ? ` · ${p.locatie}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Profile summary */}
      {userProfile?.profileComplete && (
        <div className="card card-padded" style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>👤 Jouw profiel</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {[
              { label: 'E-mail', value: currentUser.email },
              { label: 'Coach type', value: userProfile.coachType?.join(', ') },
              { label: 'T-shirt', value: userProfile.tshirtMaat },
              { label: 'Telefoon', value: userProfile.telefoon },
            ].filter(i => i.value).map(item => (
              <div key={item.label} style={{ padding: '10px 14px', background: 'var(--surface-3)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)', marginTop: 2 }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
